import React from "react";
import StatusPaginationComponent from "../../../components/Atomos/Pagination/StatusPaginationComponent";
import SectionLoadingLogo from "../../../components/Layout/LoadingAnimation/SectionLoadingLogo";
import type {
  MemberSearchEmptyReason,
  MemberSearchResponse,
  MemberSearchSectionKey,
} from "../../../Services/MemberSearch";
import MemberSearchProfessionalCard from "./MemberSearchProfessionalCard";
import MemberSearchFirmCard from "./MemberSearchFirmCard";
import MemberSearchJurisdictionCard from "./MemberSearchJurisdictionCard";

type SectionPages = {
  professionals: number;
  firms: number;
  jurisdictions: number;
};

type SectionLoading = Record<MemberSearchSectionKey, boolean>;

type MemberSearchResultsProps = {
  results: MemberSearchResponse | null;
  sectionLoading: SectionLoading;
  hasSubmitted: boolean;
  error: string | null;
  keyword: string;
  sectionPages: SectionPages;
  pageSize: number;
  visibleSections?: MemberSearchSectionKey[];
  onProfessionalPageChange: (page: number) => void;
  onFirmPageChange: (page: number) => void;
  onJurisdictionPageChange: (page: number) => void;
};

const ALL_RESULT_SECTIONS: MemberSearchSectionKey[] = [
  "professionals",
  "firms",
  "jurisdictions",
];

const SectionSeparator = () => (
  <div className="border-t border-gray-300" aria-hidden="true" />
);

const emptyResultMessage = (emptyReason?: MemberSearchEmptyReason): string => {
  if (emptyReason === "region-and-country") {
    return "No member firms are in both the selected region and country.";
  }
  if (emptyReason === "firm-outside-region") {
    return "This firm is not in the selected region.";
  }
  if (emptyReason === "firm-outside-country") {
    return "This firm is not in the selected country.";
  }
  if (emptyReason === "firm-outside-region-and-country") {
    return "This firm is not in both the selected region and country.";
  }
  return "No records found! Please refine your search query.";
};

const shouldShowSectionPagination = (
  total: number,
  currentPage: number,
  pageSize: number,
): boolean => currentPage > 1 || total > pageSize;

const SectionPaginationControls: React.FC<{
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}> = ({ totalCount, currentPage, pageSize, onPageChange }) => {
  if (!shouldShowSectionPagination(totalCount, currentPage, pageSize)) {
    return null;
  }

  return (
    <StatusPaginationComponent
      totalCount={totalCount}
      currentPage={currentPage}
      recordsPerPage={pageSize}
      onPageChange={(page) => onPageChange(page)}
    />
  );
};

const MemberSearchResults: React.FC<MemberSearchResultsProps> = ({
  results,
  sectionLoading,
  hasSubmitted,
  error,
  keyword,
  sectionPages,
  pageSize,
  onProfessionalPageChange,
  onFirmPageChange,
  onJurisdictionPageChange,
  visibleSections = ALL_RESULT_SECTIONS,
}) => {
  const professionals = results?.professionals.items ?? [];
  const firms = results?.firms.items ?? [];
  const jurisdictions = results?.jurisdictions.items ?? [];
  const showProfessionals = visibleSections.includes("professionals");
  const showFirms = visibleSections.includes("firms");
  const showJurisdictions = visibleSections.includes("jurisdictions");

  if (!hasSubmitted) {
    return (
      <p className="py-6 text-base italic text-gray-font md:text-lg">
        Enter at least one search field above and click Search to view results.
      </p>
    );
  }

  if (error && !results) {
    return <p className="py-6 text-base text-primary md:text-lg">{error}</p>;
  }

  const hasAnyResults =
    (showProfessionals && professionals.length > 0) ||
    (showFirms && firms.length > 0) ||
    (showJurisdictions && jurisdictions.length > 0);

  const initialLoading =
    visibleSections.some((section) => sectionLoading[section]) && !hasAnyResults;

  if (initialLoading) {
    return <SectionLoadingLogo />;
  }

  if (!results) {
    return null;
  }

  const catalogPending =
    (showFirms && sectionLoading.firms) ||
    (showJurisdictions && sectionLoading.jurisdictions);
  const professionalsPending = showProfessionals && sectionLoading.professionals;
  const allSectionsEmpty =
    (!showProfessionals ||
      (professionals.length === 0 && results.professionals.total === 0)) &&
    (!showFirms || (firms.length === 0 && results.firms.total === 0)) &&
    (!showJurisdictions ||
      (jurisdictions.length === 0 && results.jurisdictions.total === 0));

  if (!professionalsPending && !catalogPending && allSectionsEmpty) {
    return (
      <p className="py-6 text-base italic text-gray-font md:text-lg">
        {emptyResultMessage(results.emptyReason)}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-8 pb-4 py-8">
      {error ? (
        <p className="text-sm text-primary" aria-live="polite">
          {error}
        </p>
      ) : null}
      {showProfessionals ? (
      <section className="flex flex-col gap-6">
        <h3 className="font-decimal text-xl font-medium text-Default md:text-2xl">
          Professionals
        </h3>
        <SectionPaginationControls
          totalCount={results.professionals.total}
          currentPage={sectionPages.professionals}
          pageSize={pageSize}
          onPageChange={onProfessionalPageChange}
        />
        {sectionLoading.professionals ? (
          <SectionLoadingLogo />
        ) : professionals.length > 0 ? (
          <div className="grid grid-cols-1 items-stretch gap-8 md:grid-cols-2 xl:grid-cols-3">
            {professionals.map((person) => (
              <MemberSearchProfessionalCard
                key={person.id}
                person={person}
                keyword={keyword}
              />
            ))}
          </div>
        ) : (
          <p className="text-base italic text-gray-font md:text-lg">
            No professionals found.
          </p>
        )}
        <SectionPaginationControls
          totalCount={results.professionals.total}
          currentPage={sectionPages.professionals}
          pageSize={pageSize}
          onPageChange={onProfessionalPageChange}
        />
      </section>
      ) : null}

      {showProfessionals && showFirms ? <SectionSeparator /> : null}

      {showFirms ? (
      <section>
        <h3 className="mb-4 font-decimal text-xl font-medium text-Default md:text-2xl">
          Firms
        </h3>
        {sectionLoading.firms ? (
          <SectionLoadingLogo />
        ) : firms.length > 0 ? (
          <div className="grid grid-cols-1 items-stretch gap-10 md:grid-cols-2 xl:grid-cols-3">
            {firms.map((firm) => (
              <MemberSearchFirmCard key={firm.id} firm={firm} keyword={keyword} />
            ))}
          </div>
        ) : (
          <p className="text-base italic text-gray-font md:text-lg">
            No firms found.
          </p>
        )}
        <SectionPaginationControls
          totalCount={results.firms.total}
          currentPage={sectionPages.firms}
          pageSize={pageSize}
          onPageChange={onFirmPageChange}
        />
      </section>
      ) : null}

      {showFirms && showJurisdictions ? <SectionSeparator /> : null}

      {showJurisdictions ? (
      <section>
        <h3 className="mb-4 font-decimal text-xl font-medium text-Default md:text-2xl">
          Jurisdictions
        </h3>
        {sectionLoading.jurisdictions ? (
          <SectionLoadingLogo />
        ) : jurisdictions.length > 0 ? (
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 xl:grid-cols-3">
            {jurisdictions.map((jurisdiction) => (
              <MemberSearchJurisdictionCard
                key={jurisdiction.id}
                jurisdiction={jurisdiction}
              />
            ))}
          </div>
        ) : (
          <p className="text-base italic text-gray-font md:text-lg">
            No jurisdictions found.
          </p>
        )}
        <SectionPaginationControls
          totalCount={results.jurisdictions.total}
          currentPage={sectionPages.jurisdictions}
          pageSize={pageSize}
          onPageChange={onJurisdictionPageChange}
        />
      </section>
      ) : null}
    </div>
  );
};

export default MemberSearchResults;
